<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
import { useUISettings } from 'dashboard/composables/useUISettings';
import globals from 'dashboard/constants/globals';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  nested: { type: Boolean, default: false },
  component: { type: Function, default: null },
  count: { type: Number, default: null },
  children: { type: Array, default: () => [] },
  folderId: { type: [String, Number], default: null },
  color: { type: String, default: null },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();
const { uiSettings, updateUISettings } = useUISettings();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

const hasChildren = computed(() => {
  return Array.isArray(props.children) && props.children.length > 0;
});

const isFolderCollapsed = computed(() => {
  if (!hasChildren.value || !props.folderId) return false;
  return uiSettings.value.collapsed_folders?.[props.folderId] || false;
});

const toggleFolderCollapse = () => {
  if (!hasChildren.value || !props.folderId) return;
  const currentCollapsedFolders = uiSettings.value.collapsed_folders || {};
  updateUISettings({
    collapsed_folders: {
      ...currentCollapsedFolders,
      [props.folderId]: !isFolderCollapsed.value,
    },
  });
};

const itemClass = computed(() => {
  if (!props.color) return 'after:bg-transparent after:border-n-slate-4';

  return `folder folder-${props.color}`;
});

const colorClasses = computed(() => {
  if (!props.color || !globals.COLORS[props.color]) return {};

  const colorConfig = globals.COLORS[props.color];
  const lightColor = colorConfig.light;
  const darkColor = colorConfig.dark;

  return {
    [`--folder-color-light`]: lightColor,
    [`--folder-color-dark`]: darkColor,
  };
});

const bubbleClasses = computed(() => {
  const classes = {
    active: props.active,
    'max-w-[12rem]': !props.nested,
    'max-w-[11rem]': props.nested,
  };

  if (!props.color) {
    classes['n-blue-text bg-n-alpha-2'] = props.active;
  }

  return classes;
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ltr:pl-2 rtl:pr-2 rtl:mr-2 ltr:ml-2 relative text-n-slate-11 child-item before:bg-n-slate-4 before:left-0 rtl:before:right-0"
    :class="itemClass"
    :style="colorClasses"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex h-8 items-center gap-0.5 px-1 py-1 rounded-lg hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70 group"
      :class="bubbleClasses"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        :label
        :icon
        :active
        :count
      />
      <template v-else>
        <Icon v-if="icon" :icon="icon" class="size-4 inline-block" />
        <div class="flex-1 truncate min-w-0 folder-label">{{ label }}</div>
        <button
          v-if="hasChildren"
          class="p-0 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded transition-all"
          @click="toggleFolderCollapse"
        >
          <span
            :class="
              isFolderCollapsed
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-up'
            "
          />
        </button>
        <span
          v-if="count !== null"
          class="px-1 py-0 rounded-md text-xxs bg-slate-50 dark:bg-slate-700"
        >
          {{ count }}
        </span>
      </template>
    </component>
  </Policy>
</template>

<style lang="scss">
.folder {
  &.before\:bg-n-slate-4 {
    &:before {
      background-color: var(--folder-color-light);
    }
    &:after {
      border-color: var(--folder-color-light);
    }
  }

  .folder-label {
    color: var(--folder-color-light);
  }

  .active {
    background-color: color-mix(
      in srgb,
      var(--folder-color-light) 15%,
      rgba(255, 255, 255, 0.5) 85%
    );
  }
}

.dark .folder {
  &.before\:bg-n-slate-4 {
    &:before {
      background-color: var(--folder-color-dark);
    }
    &:after {
      border-color: var(--folder-color-dark);
    }
  }

  .folder-label {
    color: var(--folder-color-dark);
  }

  .active {
    background-color: color-mix(
      in srgb,
      var(--folder-color-dark) 15%,
      rgba(50, 50, 50, 0.5) 85%
    );
  }
}
</style>
